import { useMemo } from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  YAxis,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from 'recharts'
import { calculateInvestmentProjections } from './_utils/investmentCalculator'
import { ChartTooltip } from './_components/ChartToolTip'
import { Stack } from '@mui/material'
import { ChildrenMilestones } from './_components/ChildrenMilestones'

interface Props {
  theme?: 'light' | 'dark'
  series: ReturnType<typeof calculateInvestmentProjections>
  months: number
  slug: string
}

export const Chart = ({ theme = 'dark', series, months, slug }: Props) => {
  const {
    timeInMonths,
    totalInvested,
    earlyWithdrawValue,
    bonashipValue,
    inflationValue,
    cashValue,
    bankSavingsValue,
    rates,
  } = series

  const lightMode = theme === 'light'

  const isSavingsChildren = slug === 'buducnost-mojich-deti'
  // const isSavingsRetirement = slug === 'zabezpecenie-financii-na-dochodok'
  const isSavingsBeatInflation = slug === 'sporte-a-porazte-inflaciu'

  // data for Recharts
  const data = useMemo(
    () =>
      timeInMonths.map((t, i) => ({
        month: t,

        invested: totalInvested[i],

        earlyWithdrawVal: earlyWithdrawValue[i],
        bonVal: bonashipValue[i],
        infVal: inflationValue[i],
        casVal: cashValue[i],
        bankVal: bankSavingsValue[i],
        rates: rates,
        slug: slug,
      })),
    [
      timeInMonths,
      totalInvested,
      earlyWithdrawValue,
      bonashipValue,
      inflationValue,
      cashValue,
      bankSavingsValue,
      rates,
      slug,
    ]
  )

  const maxAbs = useMemo(() => {
    let m = 0
    for (const d of data) {
      m = Math.max(m, d.bonVal || 0)
    }
    return m * 1.45
  }, [data])

  const isAnimationSafe = months > 5
  const xTicks = [60, 120, 180, 240, 300, 360, 420, 480]

  return (
    <Stack
      sx={{
        '& svg': { outline: 'none !important' },
        '& .recharts-area-curve': {
          filter: 'drop-shadow(0 0 6px currentColor)',
        },
      }}>
      <div className="chart-scope relative">
        <div className="w-full h-[222px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              className="relative"
              data={data}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              {xTicks.map((xTick) => (
                <ReferenceLine
                  key={`xTick-${xTick}`}
                  x={xTick}
                  stroke={lightMode ? '#E3E3E3' : '#353538'}
                  strokeWidth={1}
                />
              ))}

              <defs>
                <linearGradient
                  id="gradientEarlyWithdraw"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop
                    offset="5%"
                    stopColor={isSavingsBeatInflation ? '#8fffa3' : '#FBE507'}
                    stopOpacity={lightMode ? 1 : 0.1}
                  />
                  <stop
                    offset="95%"
                    stopColor={isSavingsBeatInflation ? '#8fffa3' : '#FBE507'}
                    stopOpacity={lightMode ? 1 : 0}
                  />
                </linearGradient>
                <linearGradient
                  id="gradientBonaship"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop
                    offset="5%"
                    stopColor={isSavingsBeatInflation ? '#8fffa3' : '#FBE507'}
                    stopOpacity={lightMode ? 1 : 0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={isSavingsBeatInflation ? '#8fffa3' : '#FBE507'}
                    stopOpacity={lightMode ? 1 : 0}
                  />
                </linearGradient>

                <linearGradient
                  id="gradientInflation"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop
                    offset="5%"
                    stopColor={isSavingsBeatInflation ? '#FF5B5B' : '#FBE507'}
                    stopOpacity={lightMode ? 1 : 1}
                  />
                  <stop
                    offset="95%"
                    stopColor={isSavingsBeatInflation ? '#FF5B5B' : '#FBE507'}
                    stopOpacity={lightMode ? 1 : 0}
                  />
                </linearGradient>

                <linearGradient
                  id="gradientBankSavings"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop
                    offset="5%"
                    stopColor="#F4F4FF"
                    stopOpacity={
                      isSavingsBeatInflation ? 0 : lightMode ? 1 : 0.2
                    }
                  />
                  <stop
                    offset="95%"
                    stopColor="#F4F4FF"
                    stopOpacity={isSavingsBeatInflation ? 0 : lightMode ? 1 : 0}
                  />
                </linearGradient>
                <linearGradient
                  id="gradientHotovost"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop
                    offset="5%"
                    stopColor="#898a8e"
                    stopOpacity={isSavingsBeatInflation ? 0 : lightMode ? 1 : 1}
                  />
                  <stop
                    offset="95%"
                    stopColor="#898a8e"
                    stopOpacity={isSavingsBeatInflation ? 0 : lightMode ? 1 : 0}
                  />
                </linearGradient>
              </defs>

              <YAxis
                domain={[0, maxAbs]}
                axisLine={false}
                tickLine={false}
                tick={false}
                width={0}
              />

              <Area
                type="monotone"
                dataKey="earlyWithdrawVal"
                name="EarlyWithdraw"
                stroke={isSavingsBeatInflation ? '#8fffa3' : '#FBE507'}
                strokeWidth={1.5}
                isAnimationActive={isAnimationSafe}
                strokeDasharray="2 2"
                fill="url(#gradientEarlyWithdraw)"
                activeDot={{
                  r: 5,
                  fill: isSavingsBeatInflation ? '#45F55A' : '#FBE507',
                  stroke: isSavingsBeatInflation ? '#45F55A' : '#FBE507',
                  strokeWidth: 1,
                }}
              />
              <Area
                type="monotone"
                dataKey="bonVal"
                name="Bonaship"
                stroke={isSavingsBeatInflation ? '#8fffa3' : '#FBE507'}
                strokeWidth={1.5}
                isAnimationActive={isAnimationSafe}
                fill="url(#gradientBonaship)"
                activeDot={{
                  r: 5,
                  fill: isSavingsBeatInflation ? '#45F55A' : '#FBE507',
                  stroke: isSavingsBeatInflation ? '#45F55A' : '#FBE507',
                  strokeWidth: 1,
                }}
              />
              {isSavingsBeatInflation && (
                <Area
                  type="monotone"
                  dataKey="infVal"
                  name="Inflation"
                  stroke="#F4F4FF"
                  strokeWidth={1}
                  fill="url(#gradientInflation)"
                  isAnimationActive={isAnimationSafe} // Use our safety flag
                  strokeDasharray="2 2"
                  activeDot={{
                    r: 4,
                    fill: '#F4F4FF',
                    stroke: '#F4F4FF',
                    strokeWidth: 1,
                  }}
                />
              )}
              {(isSavingsChildren || isSavingsBeatInflation) && (
                <Area
                  type="monotone"
                  dataKey="bankVal"
                  name="BankSavings"
                  stroke={isSavingsBeatInflation ? '#ff5c5c' : '#F4F4FF'}
                  strokeWidth={1.5}
                  fill="url(#gradientBankSavings)"
                  isAnimationActive={isAnimationSafe} // Use our safety flag
                  activeDot={{
                    r: 4,
                    fill: isSavingsBeatInflation ? '#ff5c5c' : '#F4F4FF',
                    stroke: isSavingsBeatInflation ? '#ff5c5c' : '#F4F4FF',
                    strokeWidth: 1,
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="casVal"
                name="Cash"
                stroke={isSavingsBeatInflation ? '#ff8c8c' : '#898a8e'}
                strokeWidth={1.5}
                fill="url(#gradientHotovost)"
                isAnimationActive={isAnimationSafe} // Use our safety flag
                activeDot={{
                  r: 4,
                  fill: isSavingsBeatInflation ? '#ff8c8c' : '#898a8e',
                  stroke: isSavingsBeatInflation ? '#ff8c8c' : '#898a8e',
                  strokeWidth: 1,
                }}
              />

              {isSavingsChildren && <ChildrenMilestones maxAbs={maxAbs} />}

              <RechartsTooltip
                content={<ChartTooltip />}
                cursor={{
                  stroke: '#88898D',
                  strokeWidth: 1,
                  strokeDasharray: '5 5',
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Stack>
  )
}

export default Chart
